import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  IconButton,
  Paper,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import RestoreIcon from "@mui/icons-material/Restore";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ArticleIcon from "@mui/icons-material/Article";
import { withTranslation } from "react-i18next";
import axios from "axios";
import { invalidateTemplateCache } from "../maintenances/TemplatePicker";

const AVAILABLE_VARIABLES = [
  { key: "vehicle_reg", example: "ABC-123" },
  { key: "customer_name", example: "Matti Meikäläinen" },
  { key: "organization_name", example: "Autokorjaamo Oy" },
  { key: "organization_phone", example: "040 123 4567" },
  { key: "total_price", example: "150,00" },
  { key: "date", example: "29.3.2026" },
  { key: "vehicle_make", example: "Toyota" },
  { key: "vehicle_model", example: "Corolla" },
];

function CustomerNoteTemplatesTab({ t }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState(null);
  const [resetConfirm, setResetConfirm] = useState(false);

  const fetchTemplates = async () => {
    try {
      const response = await axios.get("/api/customer-note-templates/");
      setTemplates(response.data);
    } catch (err) {
      console.error("Failed to fetch templates", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleTemplateChange = (index, field, value) => {
    setTemplates((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  const handleSaveTemplate = async (template) => {
    setSaving(true);
    try {
      if (template.id) {
        await axios.patch(`/api/customer-note-templates/${template.id}/`, {
          name: template.name,
          body: template.body,
          sort_order: template.sort_order,
        });
      } else {
        const response = await axios.post("/api/customer-note-templates/", {
          name: template.name,
          body: template.body,
          sort_order: template.sort_order || 0,
        });
        setTemplates((prev) =>
          prev.map((t) => (t === template ? response.data : t))
        );
      }
      invalidateTemplateCache();
      setSnackbar({ open: true, message: t("Templates saved"), severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.detail || t("Error"), severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTemplate = async (template, index) => {
    if (deleteConfirmIndex !== index) {
      setDeleteConfirmIndex(index);
      return;
    }
    setDeleteConfirmIndex(null);
    try {
      if (template.id) {
        await axios.delete(`/api/customer-note-templates/${template.id}/`);
      }
      setTemplates((prev) => prev.filter((_, i) => i !== index));
      invalidateTemplateCache();
      setSnackbar({ open: true, message: t("Template deleted"), severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: t("Error"), severity: "error" });
    }
  };

  const handleAddTemplate = () => {
    setTemplates((prev) => [
      ...prev,
      { name: "", body: "", sort_order: prev.length, _isNew: true },
    ]);
  };

  const handleResetDefaults = async () => {
    if (!resetConfirm) {
      setResetConfirm(true);
      return;
    }
    setResetConfirm(false);
    try {
      const response = await axios.post("/api/customer-note-templates/reset_defaults/");
      setTemplates(response.data);
      invalidateTemplateCache();
      setSnackbar({ open: true, message: t("Templates saved"), severity: "success" });
    } catch (err) {
      setSnackbar({ open: true, message: t("Error"), severity: "error" });
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <ArticleIcon sx={{ color: "secondary.main" }} />
          <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            {t("Customer note templates")}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {t("templates_tab_description")}
        </Typography>
      </Box>

      {/* Variables reference */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 3, bgcolor: "grey.50", borderRadius: "8px" }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 1.5 }}>
          <InfoOutlinedIcon sx={{ fontSize: 18, color: "text.secondary" }} />
          <Typography variant="subtitle2" sx={{ fontWeight: 600, color: "text.secondary" }}>
            {t("templates_variables_title")}
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: "text.secondary", mb: 1.5 }}>
          {t("templates_variables_description")}
        </Typography>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75 }}>
          {AVAILABLE_VARIABLES.map((v) => (
            <Tooltip key={v.key} title={`${t("Example")}: ${v.example}`} arrow>
              <Chip
                label={`{{${v.key}}}`}
                size="small"
                variant="outlined"
                sx={{
                  fontFamily: "monospace",
                  fontSize: "12px",
                  borderColor: "secondary.light",
                  color: "secondary.main",
                }}
              />
            </Tooltip>
          ))}
        </Box>
      </Paper>

      {/* Templates list */}
      <Grid container spacing={2}>
        {templates.map((template, index) => (
          <Grid item xs={12} key={template.id || `new-${index}`}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                borderRadius: "8px",
                borderColor: template._isNew ? "secondary.light" : "divider",
              }}
            >
              <Box sx={{ display: "flex", gap: 1, mb: 1.5, alignItems: "center" }}>
                <TextField
                  size="small"
                  label={t("Template name")}
                  placeholder={t("templates_name_placeholder")}
                  value={template.name}
                  onChange={(e) => handleTemplateChange(index, "name", e.target.value)}
                  sx={{ flex: 1 }}
                />
                <Tooltip title={deleteConfirmIndex === index ? t("Click to confirm") : t("Delete")}>
                  <IconButton
                    color={deleteConfirmIndex === index ? "error" : "default"}
                    onClick={() => handleDeleteTemplate(template, index)}
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
              <TextField
                fullWidth
                multiline
                rows={4}
                size="small"
                label={t("Template body")}
                placeholder={t("templates_body_placeholder")}
                value={template.body}
                onChange={(e) => handleTemplateChange(index, "body", e.target.value)}
                helperText={t("templates_body_helper")}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => handleSaveTemplate(template)}
                  disabled={saving || !template.name.trim() || !template.body.trim()}
                  sx={{
                    borderRadius: "8px",
                    textTransform: "none",
                    fontWeight: 500,
                    fontFamily: "'Plus Jakarta Sans', sans-serif",
                  }}
                >
                  {saving ? <CircularProgress size={16} sx={{ mr: 0.5 }} /> : null}
                  {t("Save")}
                </Button>
              </Box>
            </Paper>
          </Grid>
        ))}

        {templates.length === 0 && (
          <Grid item xs={12}>
            <Paper
              variant="outlined"
              sx={{
                p: 4,
                textAlign: "center",
                borderRadius: "8px",
                borderStyle: "dashed",
              }}
            >
              <ArticleIcon sx={{ fontSize: 40, color: "text.disabled", mb: 1 }} />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {t("No templates")}
              </Typography>
            </Paper>
          </Grid>
        )}
      </Grid>

      {/* Action buttons */}
      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 3 }}>
        <Button
          startIcon={<AddIcon />}
          onClick={handleAddTemplate}
          variant="outlined"
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {t("Add template")}
        </Button>
        <Button
          startIcon={<RestoreIcon />}
          onClick={handleResetDefaults}
          color={resetConfirm ? "error" : "inherit"}
          variant={resetConfirm ? "contained" : "text"}
          sx={{
            borderRadius: "8px",
            textTransform: "none",
            fontWeight: 500,
            fontFamily: "'Plus Jakarta Sans', sans-serif",
          }}
        >
          {resetConfirm ? t("Click to confirm") : t("Reset to defaults")}
        </Button>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default withTranslation()(CustomerNoteTemplatesTab);
